import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Dialog,
  Box,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";
import DomainIcon from "@mui/icons-material/Domain";
import MicIcon from "@mui/icons-material/Mic";
import BuildIcon from "@mui/icons-material/Build";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL, AI_ENGINE_URL } from "../utils/apiConfig";
import { authFetch } from "../utils/authHelpers";
import { FORMAAT_OMZETTEN_URL } from "../config/flowUrl";

const RECENT_KEY = "globalSearch_recent";
const MAX_RECENT = 5;
const MAX_RESULTS = 5;

const CATEGORY_CONFIG = {
  projects: { icon: FolderIcon, color: "#6366f1", label: "Projecten" },
  tasks: { icon: AssignmentIcon, color: "#f59e0b", label: "Taken" },
  people: { icon: PersonIcon, color: "#10b981", label: "Personen" },
  accounts: { icon: DomainIcon, color: "#0ea5e9", label: "Accounts" },
  meetings: { icon: MicIcon, color: "#8b5cf6", label: "Meetings" },
  tools: { icon: BuildIcon, color: "#6b7280", label: "Tools" },
};

/**
 * Volgorde van de tabbladen ná "Alles".
 *
 * Tools staat vooraan omdat dit zoekvenster in de praktijk vooral als sprong-naar-scherm
 * wordt gebruikt (Cmd+K, "sales", enter). Het stond laatst van zes, en zes tabbladen
 * passen niet in een dialoog van 600px — juist het tabblad dat je nodig had viel weg
 * achter de rand.
 */
const TAB_VOLGORDE = ["tools", "projects", "accounts", "tasks", "meetings", "people"];

/**
 * Hoe goed past deze tool bij wat je typt? Lager is beter.
 *
 * Zonder rangschikking bepaalde de volgorde in `TOOLS` de uitslag: bij "sales" kwam elk
 * scherm met "sales" ergens in zijn trefwoorden even hoog uit als het Sales Dashboard
 * zelf. Een treffer in de títeI is bijna altijd wat je bedoelt, en begint de titel er ook
 * mee, dan is het zo goed als zeker de juiste.
 */
function toolRang(tool, q) {
  const titel = tool.title.toLowerCase();
  if (titel.startsWith(q)) return 0;
  if (titel.includes(q)) return 1;
  return 2;
}

// Houd in sync met NAV_ITEMS in /config/navigation.js (single source of truth voor pagina's)
// Geëxporteerd zodat de navigatietest kan bewaken dat elk scherm hier onder zijn
// eigen naam in staat — een hernoemde pagina die hier oud blijft heten is onvindbaar.
export const TOOLS = [
  // Mijn werk / Planning
  { title: "Vandaag", keywords: "vandaag today dag overzicht agenda", link: "/vandaag" },
  { title: "Mijn widgets", keywords: "home startpagina widgets dashboard cockpit snelkoppelingen", link: "/home" },
  { title: "Overleggen", keywords: "overleg overleggen mijn week mt-punt commercieel scorecard besluiten rating pakket", link: "/overleg" },
  { title: "Actieregister", keywords: "acties actieregister todo overleg wie doet wat wanneer te laat", link: "/overleg/acties" },
  { title: "Verbeterregister", keywords: "verbeterregister verbeterpunten verbeteren kwaliteit iso evaluatie maatregelen geborgd leerpunten", link: "/verbeterregister" },
  { title: "Mijn Dashboard", keywords: "dashboard mijn werk taken todo overzicht", link: "/dashboard" },
  { title: "Slim Uren", keywords: "uren tijd registratie timesheet slim ai", link: "/slim-uren" },
  { title: "Dagstart", keywords: "dagstart mijn week overzicht weekplanning agenda achterstallig standup", link: "/dagstart" },
  { title: "Planningsgrid per project", keywords: "planningsgrid overzicht per project taken deliverables planning prio deadlines uren airtable", link: "/planning/overzicht-per-project" },
  { title: "Weekoverzicht per PL", keywords: "overzicht per project taken deliverables planning prio deadlines uren airtable", link: "/planning/weekoverzicht-pl" },
  { title: "Richtprijzen klant-assistent", keywords: "richtprijzen prijzen tarieven klant assistent account offerte indicatie wat mag genoemd worden", link: "/richtprijzen" },
  { title: "Weekbord", keywords: "weekbord weekplanning taskboard task board planning taken", link: "/task-board" },
  { title: "Capaciteit", keywords: "capaciteit team planning beschikbaarheid", link: "/capacity" },
  { title: "Rooster", keywords: "rooster planner todo to-do planner to-do planning taken inplannen wie doet wat", link: "/planner-todo" },
  { title: "Nog in te plannen", keywords: "inplannen ongepland montage animatie niet toegewezen deadline check offerte-uren", link: "/planning/inplannen" },
  { title: "Materialen reserveren", keywords: "materiaal materialen reservering camera lens apparatuur", link: "/MaterialReservation" },
  { title: "Draaidagen", keywords: "draaidagen draaidag shoot shoots opnamedag buitenland buitenlandshoots crew kalender", link: "/draaidagen" },

  // Projecten / Accounts
  { title: "Projecten", keywords: "project projecten overzicht klant opdracht", link: "/projects" },
  { title: "Notities", keywords: "notes notities notitie aantekening krabbel telefoon klant", link: "/notes" },
  { title: "Accounts", keywords: "accounts klanten bedrijven relaties", link: "/accounts" },
  { title: "Marketing OS", keywords: "marketing os campagne content", link: "/marketing" },
  { title: "Klantaanvragen", keywords: "aanvragen klantaanvragen portaal flow briefing item ondertiteling draaidag", link: "/aanvragen-portaal" },
  { title: "Contentkalender", keywords: "contentkalender social posts planable planner akkoord klant instagram linkedin", link: "/contentkalender" },

  // Productie
  { title: "Callsheets", keywords: "callsheet call sheet draaidag productie planning", link: "/callsheets" },
  { title: "Script Breakdown", keywords: "script breakdown scenario pre-productie", link: "/script-breakdown" },
  { title: "Productie Breakdown", keywords: "productie breakdown regelen cast locatie props voertuig wardrobe sfx opties pre-productie", link: "/productie-breakdown" },
  { title: "AI Voortgang", keywords: "ai voortgang akkoord klant fase script storyboard animatic eindversie revisie wijziging klantportaal goedkeuring", link: "/ai-voortgang" },
  { title: "Video Edit QA", keywords: "video edit qa review feedback post-productie", link: "/video-qa" },
  { title: "MXF Check", keywords: "mxf tv aanlevering screenforce spotdeli loudness commercial billboard xdcam", link: "/mxf-check" },
  { title: "Ondertiteling", keywords: "ondertitel ondertiteling subtitle hub", link: "/ondertiteling" },
  { title: "SRT Generator", keywords: "srt generator ondertitels genereren transcriberen vertalen", link: "/SRTGenerator" },
  { title: "SRT Beheer", keywords: "srt dashboard ondertitel beheer batch", link: "/SRTDashboard" },
  { title: "Video Downloader", keywords: "video downloader download youtube vimeo", link: "/video-downloader" },
  { title: "Titel Generator", keywords: "titel name title generator naamtitel", link: "/name-title-generator" },
  { title: "Formaat omzetten", keywords: "formaat omzetten reframe 9:16 4:5 1:1 verticaal vierkant kader crop flow", link: "/formaat-omzetten", externalUrl: FORMAAT_OMZETTEN_URL },
  { title: "PowerPoint Video Replacer", keywords: "pptx powerpoint video replacer presentatie vervangen", link: "/pptx-video-replacer" },
  { title: "Narrowcasting", keywords: "narrowcasting display schermen signage", link: "/narrowcasting" },
  { title: "PDF Combiner", keywords: "pdf combiner combineer samenvoegen merge combine document documenten", link: "/pdf-combiner" },

  // AI
  { title: "AI Chat", keywords: "ai chat gpt assistent chatbot", link: "/ai-chat" },
  { title: "GPT Bouwer", keywords: "gpt bouwer maker custom assistent", link: "/gpt-maker" },
  { title: "Explainer Studio", keywords: "explainer studio script schrijven uitleg ai", link: "/explainer-script" },
  { title: "Scriptstijlen", keywords: "scriptstijl schrijfstijl explainer voice-over presentator animatie richtlijnen voorbeelden script generator", link: "/scriptstijlen" },
  { title: "Animatie leren", keywords: "animatie after effects illustrator aep ai astra skill klant leren werkwijze animator", link: "/animatie-leren" },
  { title: "Animatie-jobs", keywords: "animatie job storyboard voice-over astra studio after effects concept beoordelen wachtrij", link: "/animatie-jobs" },
  { title: "Meetings", keywords: "meetings meeting notes vergadering transcriptie recorder", link: "/MeetingDashboard" },
  { title: "Video Search", keywords: "video zoeken search frame.io zoekmachine", link: "/video-search" },
  { title: "Inspiratie", keywords: "inspiratie referentie voorbeeld inspiration", link: "/inspiration" },
  { title: "Effie Awards Gids", keywords: "effie awards gids guide creatief", link: "/effie-guide" },
  { title: "Avatar Chat", keywords: "avatar chat ai gesprek video", link: "/avatar-chat" },
  { title: "AI-beelden", keywords: "ai beelden higgsfield video genereren moodframe storyboard seedance kling", link: "/ai-beelden" },

  // Operations
  { title: "Sales Dashboard", keywords: "sales verkoop offerte pipeline dashboard", link: "/sales-dashboard" },
  { title: "Revenue Forecast", keywords: "forecast omzet revenue prognose financieel", link: "/revenue-forecast" },
  { title: "Budget", keywords: "budget target targets omzet account reforecast volgend jaar excel revenue pijplijn", link: "/budget" },
  { title: "OHW Check", keywords: "ohw onderhanden werk check kpi", link: "/ohw-check" },
  { title: "Uren Controle", keywords: "uren controle check kpi management", link: "/uren-check" },
  { title: "Klanttevredenheid", keywords: "klanttevredenheid enquete survey nps feedback", link: "/enquete-dashboard" },
  { title: "Sleuteloverzicht", keywords: "sleutels sleutel keytags keytag tag pand toegang deur badge", link: "/sleutels" },
  { title: "Werving", keywords: "werving vacature vacatures sollicitatie sollicitaties sollicitant kandidaat kandidaten ikwilwerken scorecard beoordelen ats homerun", link: "/werving" },

  // Commercieel
  { title: "Offerte Generator", keywords: "offerte offertes generator gripp concept aanmaken quote", link: "/offertes" },
  { title: "Sales Prospects", keywords: "sales prospects leads acquisitie pipeline", link: "/sales/prospects" },
  { title: "Achmea factuur-export", keywords: "achmea factuur export facturatie", link: "/achmea-export" },
  { title: "Opdracht-mix", keywords: "opdracht mix verdeling omzet analyse", link: "/opdracht-mix" },
  { title: "Strategie", keywords: "strategie koers pijlers retainer", link: "/strategy" },
  { title: "Werkdruk PL's & team", keywords: "werkdruk pl pls projectleider projectleiders projecten per pl drukte team crew regisseur camera uitvoerders mt dashboard sturing joris kpi", link: "/joris-dashboard" },
  { title: "Declarabiliteit", keywords: "declarabiliteit declarabel declarabele uren budget binnen boven gripp medewerker maand productiviteit norm 65", link: "/declarabiliteit" },

  // Assistent & planning
  { title: "Frits (mail-assistent)", keywords: "frits mail assistent e-mail triage concepten inbox", link: "/mail-assistent" },
  { title: "AI-mail", keywords: "ai mail categorie outlook doorsturen voorstel concept factuur", link: "/ai-mail" },
  { title: "Overdracht (PL)", keywords: "overdracht projectleider dossier handover", link: "/overdracht" },
  { title: "Weekplan", keywords: "weekplan week planning persoonlijk", link: "/weekplan" },
  { title: "Projecttaken", keywords: "mijn taken projecttaken takenlijst per project deadline", link: "/mijn-taken" },
  { title: "Privé-to-do's", keywords: "todo to-do microsoft to do snel taken kladblok prive", link: "/mijn-todos" },
  { title: "Draaidag-planner", keywords: "draaidag planner shoot planning crew", link: "/draaidag-planner" },
  { title: "Slimme toewijzing", keywords: "slimme toewijzing taken verdelen ai", link: "/slimme-toewijzing" },
  { title: "Corvee", keywords: "corvee schoonmaak taken rooster", link: "/planning/corvee" },
  { title: "Voertuigen & Brandstof", keywords: "voertuigen brandstof auto bus tanken", link: "/planning/voertuigen" },
  { title: "Leveranciersbeoordelingen", keywords: "leverancier leveranciers beoordeling review freelancer", link: "/leveranciersbeoordelingen" },
  { title: "Medewerkers", keywords: "medewerkers collega's team smoelenboek galerij foto's stagiairs wie werkt hier functie", link: "/medewerkers" },
  { title: "HR-dashboard", keywords: "hr personeel dashboard aantallen geslacht generatie contracttype verdeling team samenstelling", link: "/hr-dashboard" },
  { title: "Leveranciers", keywords: "leveranciers leverancier freelancers freelancer bedrijven zzp crew camera cameraman editor animator voice-over geluid licht visagie tarief dagtarief overeenkomsten contracten datacorrectie beoordelingen dashboard", link: "/leveranciers" },
  { title: "Materiaalbeheer", keywords: "materiaal beheer inventaris apparatuur admin reparatie kapot schade opslaglocatie", link: "/materiaal-beheer/materialen" },

  // Studio & tools
  { title: "Ondertitel Studio", keywords: "ritmo ondertitel studio karaoke render", link: "/ritmo" },
  { title: "SRT naar FCP", keywords: "srt fcp final cut omzetten convert", link: "/SRTomzettenFCP" },
  { title: "Website Preview", keywords: "website preview site voorbeeld", link: "/website-preview" },
  { title: "SharePoint opslag", keywords: "sharepoint opslag analyse opschonen storage", link: "/sharepoint-analyse" },
  { title: "Kleur Converter", keywords: "kleur color converter hex rgb cmyk", link: "/color-converter" },
  { title: "QR-generator", keywords: "qr code generator", link: "/qr-generator" },
  { title: "Sync-check (Airtable ↔ Gripp)", keywords: "sync check airtable gripp status vergelijk", link: "/sync-check" },
  { title: "Mijn profiel", keywords: "profiel voorkeuren instellingen mijn gegevens", link: "/mijn-profiel" },

  // Admin / User
  { title: "Admin Instellingen", keywords: "admin settings instellingen beheer backlog bord tickets dev pr staging", link: "/admin/settings" },
  { title: "Mijn Feedback", keywords: "feedback mijn ingediend", link: "/my-feedback" },
  { title: "Wijzigingen testen", keywords: "wijzigingen testen nieuw veranderd release changelog versie test live pr feedback", link: "/wijzigingen" },
  { title: "Bestanden delen", keywords: "bestanden delen bestand sturen versturen pdf rapport render upload download collega voor mij verstuurd", link: "/bestanden" },
];

/**
 * Global search dialog (Cmd+K / Ctrl+K).
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {string} userEmail - Current user email for task fetching
 */
const GlobalSearch = ({ open, onClose, userEmail }) => {
  const [query, setQuery] = useState("");
  // Op sleutel en niet op index: welke tabbladen er staan hangt af van wat je typt, en
  // een index verwijst dan bij de volgende letter ineens naar een andere categorie.
  const [tab, setTab] = useState("all");
  const [actief, setActief] = useState(0);
  const [data, setData] = useState({ projects: [], tasks: [], people: [], meetings: [], accounts: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Load data when dialog opens
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setTab("all");

    const fetchData = async () => {
      setLoading(true);
      try {
        // Each fetch catches its own errors to prevent one failure from blocking all results
        const safeFetch = (promise, fallback) => promise.catch(() => fallback);

        const [projectsData, tasksData, peopleData, meetingsData, accountsData] = await Promise.all([
          safeFetch(
            authFetch(`${BACKEND_URL}/Api/projects?includeArchived=true`).then((r) => r.ok ? r.json() : { projects: [] }),
            { projects: [] }
          ),
          safeFetch(
            authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail || "")}`).then((r) => r.ok ? r.json() : { tasks: [] }),
            { tasks: [] }
          ),
          safeFetch(
            authFetch(`${BACKEND_URL}/Api/personen`).then((r) => r.ok ? r.json() : []),
            []
          ),
          safeFetch(
            authFetch(`${AI_ENGINE_URL}/api/v1/meetings/`).then((r) => r.ok ? r.json() : []),
            []
          ),
          // Accounts zaten er niet in (Joris, 10 sep: "is het mogelijk dat je ook
          // zoekt op accounts"), terwijl je daar juist vaak naartoe springt.
          safeFetch(
            authFetch(`${BACKEND_URL}/Api/klanten`).then((r) => r.ok ? r.json() : { klanten: [] }),
            { klanten: [] }
          ),
        ]);
        setData({
          projects: projectsData.projects || [],
          tasks: tasksData.tasks || [],
          people: Array.isArray(peopleData) ? peopleData : peopleData.records || [],
          meetings: Array.isArray(meetingsData) ? meetingsData : [],
          accounts: accountsData.klanten || [],
        });
      } catch (err) {
        console.error("[GlobalSearch] Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, userEmail]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Filter results
  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    const qWords = q.replace(/[-_]/g, " ").split(/\s+/).filter(Boolean);
    const qNorm = qWords.join(" ");
    if (!q) return null; // Show recent searches instead

    const filterProjects = data.projects
      .filter((p) => {
        const f = p.fields || {};
        const searchable = [
          f.Projectnaam,
          f.Projectnummer,
          f.KlantnaamString,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(q);
      })
      .slice(0, MAX_RESULTS)
      .map((p) => ({
        id: p.id,
        category: "projects",
        title: p.fields?.Projectnaam || p.fields?.Projectnummer || "Project",
        subtitle: [p.fields?.Projectnummer, p.fields?.KlantnaamString]
          .filter(Boolean)
          .join(" · "),
        link: `/projects/${p.id}`,
        status: p.fields?.Status,
      }));

    const filterTasks = data.tasks
      .filter((t) => {
        const f = t.fields || {};
        const searchable = [f.NaamTaak, f.Type]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(q);
      })
      .slice(0, MAX_RESULTS)
      .map((t) => ({
        id: t.id,
        category: "tasks",
        title: t.fields?.NaamTaak || "Taak",
        subtitle: [t.fields?.Type, t.fields?.PrioNr].filter(Boolean).join(" · "),
        link: "/dashboard",
      }));

    const filterPeople = data.people
      .filter((p) => {
        const f = p.fields || {};
        const searchable = [f["Volledige naam"], f.Functie, f.Email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(q);
      })
      .slice(0, MAX_RESULTS)
      .map((p) => ({
        id: p.id,
        category: "people",
        title: p.fields?.["Volledige naam"] || "Persoon",
        subtitle: [p.fields?.Functie, p.fields?.Email]
          .filter(Boolean)
          .join(" · "),
        link: null, // No person detail page yet
      }));

    const filterAccounts = data.accounts
      .filter((a) => {
        const f = a.fields || {};
        const bedrijven = Array.isArray(f.BedrijvenNamen) ? f.BedrijvenNamen.join(" ") : "";
        return [f.Account, f.Sector, bedrijven].filter(Boolean).join(" ").toLowerCase().includes(q);
      })
      .slice(0, MAX_RESULTS)
      .map((a) => ({
        id: a.id,
        category: "accounts",
        title: a.fields?.Account || "Account",
        subtitle: [a.fields?.Sector,
          Array.isArray(a.fields?.BedrijvenNamen) && a.fields.BedrijvenNamen.length
            ? `${a.fields.BedrijvenNamen.length} bedrijven` : null]
          .filter(Boolean).join(" · "),
        link: `/accounts/${a.id}/workspace`,
      }));

    const filterMeetings = data.meetings
      .filter((m) => {
        const searchable = [
          m.title,
          m.summary,
          m.participants,
          m.host_email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return searchable.includes(q);
      })
      .slice(0, MAX_RESULTS)
      .map((m) => ({
        id: m.id,
        category: "meetings",
        title: m.title || "Meeting",
        subtitle: [
          m.meeting_date ? new Date(m.meeting_date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : null,
          m.participants,
        ]
          .filter(Boolean)
          .join(" · "),
        link: `/MeetingDashboard/${m.id}`, // FE-15: /meetings bestond niet
      }));

    const filterTools = TOOLS
      .filter((t) => {
        // "offerte-generator" moet "Offerte Generator" vinden: streepjes tellen
        // als spatie en elk los woord moet ergens voorkomen.
        const searchable = [t.title, t.keywords].join(" ").toLowerCase().replace(/[-_]/g, " ");
        return qWords.every((w) => searchable.includes(w));
      })
      .sort((a, b) => toolRang(a, qNorm) - toolRang(b, qNorm) || a.title.localeCompare(b.title))
      .map((t) => ({
        id: `tool-${t.link}`,
        category: "tools",
        title: t.title,
        subtitle: t.externalUrl ? "Tool in Flow (nieuw tabblad)" : "Tool / Pagina",
        link: t.link,
        externalUrl: t.externalUrl,
      }));

    return { projects: filterProjects, accounts: filterAccounts, tasks: filterTasks, people: filterPeople, meetings: filterMeetings, tools: filterTools };
  }, [query, data]);

  // Recent searches
  const recentSearches = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    } catch {
      return [];
    }
    // `open` bewust als afhankelijkheid: bij elke keer openen de recente zoekopdrachten
    // opnieuw uit localStorage lezen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const saveRecent = useCallback((text) => {
    try {
      const recent = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
      const updated = [text, ...recent.filter((r) => r !== text)].slice(
        0,
        MAX_RECENT
      );
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    } catch {
      // ignore
    }
  }, []);

  const handleSelect = useCallback(
    (item) => {
      if (query.trim()) saveRecent(query.trim());
      if (item.externalUrl) window.open(item.externalUrl, "_blank", "noopener,noreferrer");
      else if (item.link) navigate(item.link);
      onClose();
    },
    [query, navigate, onClose, saveRecent]
  );

  const handleRecentClick = useCallback(
    (text) => {
      setQuery(text);
    },
    []
  );

  const totalCount = results
    ? results.projects.length + results.accounts.length + results.tasks.length + results.people.length + results.meetings.length + results.tools.length
    : 0;

  // Alleen categorieën die iets opleveren krijgen een tabblad. "Personen (0)" kostte
  // evenveel breedte als een tabblad met resultaten, en duwde daarmee Tools uit beeld.
  const tabbladen = useMemo(() => {
    if (!results || totalCount === 0) return [];
    return [
      { sleutel: "all", label: `Alles (${totalCount})` },
      ...TAB_VOLGORDE
        .filter((k) => results[k].length > 0)
        .map((k) => ({ sleutel: k, label: `${CATEGORY_CONFIG[k].label} (${results[k].length})` })),
    ];
  }, [results, totalCount]);

  // Typ je door en verdwijnt het gekozen tabblad, dan val je terug op Alles in plaats van
  // op een leeg scherm (en MUI klaagt niet over een waarde die niet bestaat).
  const actieveTab = tabbladen.some((t) => t.sleutel === tab) ? tab : "all";

  // Get visible results based on tab
  const visibleResults = useMemo(() => {
    if (!results) return [];
    // Dezelfde volgorde als de tabbladen, anders staat een categorie onder "Alles" op een
    // andere plek dan de tab-strip erboven suggereert.
    if (actieveTab === "all") return TAB_VOLGORDE.flatMap((k) => results[k]);
    return results[actieveTab] || [];
  }, [results, actieveTab]);

  // Andere zoekterm of ander tabblad: de selectie hoort weer bovenaan te beginnen.
  useEffect(() => { setActief(0); }, [query, actieveTab]);

  // De gekozen regel in beeld houden als je er met de pijltjes langs loopt.
  const actiefRef = useRef(null);
  // `?.scrollIntoView?.` — jsdom kent de methode niet, en in beeld schuiven is nooit
  // belangrijk genoeg om het venster op te laten blazen.
  useEffect(() => { actiefRef.current?.scrollIntoView?.({ block: "nearest" }); }, [actief]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            position: "fixed",
            top: "15%",
            m: 0,
          },
        },
      }}
    >
      {/* Search input */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          placeholder="Zoek een scherm, project, taak, persoon of meeting..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <Chip
                  label="Esc"
                  size="small"
                  sx={{ height: 22, fontSize: 11, color: "#9ca3af" }}
                />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 },
          }}
          // Cmd+K is een sprong-naar-scherm: dan wil je typen, pijltje, enter — zonder je
          // hand naar de muis. Zonder dit moest elk resultaat aangeklikt worden.
          onKeyDown={(e) => {
            if (e.key === "Escape") { onClose(); return; }
            if (!visibleResults.length) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActief((i) => Math.min(i + 1, visibleResults.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActief((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              const keuze = visibleResults[actief];
              if (keuze?.link) { e.preventDefault(); handleSelect(keuze); }
            }
          }}
        />
      </Box>

      {/* Tabs (only when there are results) */}
      {tabbladen.length > 1 && (
        <Tabs
          value={actieveTab}
          onChange={(_, v) => setTab(v)}
          // Scrollable als vangnet: ook bij een brede zoekterm met vijf gevulde
          // categorieën moet je bij het laatste tabblad kunnen, niet erover raden.
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            minHeight: 36,
            "& .MuiTab-root": {
              minHeight: 36,
              py: 0.5,
              px: 1.25,
              textTransform: "none",
              fontSize: 13,
              minWidth: 0,
            },
          }}
        >
          {tabbladen.map((t) => (
            <Tab key={t.sleutel} value={t.sleutel} label={t.label} />
          ))}
        </Tabs>
      )}

      {/* Results list */}
      <Box sx={{ maxHeight: 420, overflowY: "auto", pb: 1 }}>
        {/* No query: show recent */}
        {!results && (
          <>
            {recentSearches.length > 0 ? (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ px: 2, pt: 1, display: "block" }}
                >
                  Recente zoekopdrachten
                </Typography>
                <List dense disablePadding>
                  {recentSearches.map((text) => (
                    <ListItem
                      key={text}
                      onClick={() => handleRecentClick(text)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f9fafb" },
                        px: 2,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <HistoryIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{text}</Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Begin met typen om te zoeken...
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Has query but no results */}
        {results && totalCount === 0 && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Geen resultaten voor &quot;{query}&quot;
            </Typography>
          </Box>
        )}

        {/* Results */}
        {results && totalCount > 0 && (
          <List dense disablePadding>
            {visibleResults.map((item, index) => {
              const cfg = CATEGORY_CONFIG[item.category];
              const Icon = cfg.icon;
              const gekozen = index === actief;
              return (
                <ListItem
                  key={`${item.category}-${item.id}`}
                  ref={gekozen ? actiefRef : null}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActief(index)}
                  sx={{
                    cursor: item.link ? "pointer" : "default",
                    backgroundColor: gekozen ? "#f3f4f6" : undefined,
                    // De gekozen regel moet je met een half oog kunnen aanwijzen; alleen
                    // een grijstint verdwijnt op een beamer of in een Teams-share.
                    boxShadow: gekozen ? "inset 3px 0 0 #6366f1" : undefined,
                    "&:hover": { backgroundColor: "#f3f4f6" },
                    px: 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon sx={{ fontSize: 20, color: cfg.color }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {item.title}
                        </Typography>
                        {item.status && (
                          <Chip
                            label={item.status}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              backgroundColor:
                                item.status === "In progress"
                                  ? "#dbeafe"
                                  : item.status === "Afgerond"
                                    ? "#dcfce7"
                                    : item.status === "On Hold"
                                      ? "#fef3c7"
                                      : "#f3f4f6",
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {item.subtitle}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Zonder deze regel is de pijltjesbediening onzichtbaar en blijft iedereen klikken. */}
      {visibleResults.length > 0 && (
        <Box sx={{ px: 2, py: 0.75, borderTop: "1px solid #f1f5f9", backgroundColor: "#fafbfc" }}>
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            ↑↓ kiezen · Enter openen · Esc sluiten
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default GlobalSearch;
